import React from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../components/icons/Icon';
import CodeViewWithLogger from '../components/CodeViewWithLogger';
import { GLOBAL_BRAIN_PROJECT_ID } from '../utils/vfsClient';
import { brainFileFromSearch } from '../utils/filesPath';
import './GlobalBrain.css';

/**
 * Global, cross-project brain — docs/tasks/memory/behaviors/preferences
 * that apply across every project, not scoped to one (the per-project
 * equivalent lives inside each project's own `brain/` folder, shown as a
 * dedicated Dashboard section — see DashboardView.jsx).
 *
 * Backed by vfs-service's reserved `_global-brain` project id (commit
 * f06e38f) — it flows through the exact same `:projectId`-scoped REST/MCP
 * routes every real project uses. Renders through `CodeViewWithLogger`
 * (Code mode's own tree+Monaco editor, not a separately-styled lookalike —
 * `serviceName="."` = the whole `_global-brain` workspace root, `vfsBacked:
 * true` + no `repositoryUrl` routes saves straight to vfs-service via
 * RepositoryTree's own vfs-direct push path instead of assuming an external
 * GitHub/GitLab remote that doesn't exist here).
 */
const GlobalBrain = () => {
  const { search } = useLocation();
  const requested = brainFileFromSearch(search);
  return (
  <div className="global-brain-page">
    <div className="global-brain-header">
      <div className="global-brain-title-row">
        <Icon color="#8B5CF6" glow={false} name="sparkles" size={22} />
        <h1 className="global-brain-title">Brain</h1>
      </div>
      <p className="global-brain-subtitle">
        Your own cross-project knowledge base — notes, tasks, and preferences that apply everywhere, not just one project. Git-versioned, same as every project's own brain.
      </p>
    </div>
    <div className="global-brain-body">
      <CodeViewWithLogger
        projectId={GLOBAL_BRAIN_PROJECT_ID}
        serviceName="."
        repositoryUrl={null}
        onFileSelect={() => {}}
        onFileChange={() => {}}
        selectedResource={{type: 'service', data: {name: '.', vfsBacked: true}}}
        syncUrlOnFileSelect={false}
        // The Brain page has no WebSocket live-sync (that's wired only in
        // Canvas.jsx, which this page doesn't render) and no dedicated push/
        // pull button either — its content is written almost entirely by
        // chat tool calls (vfs_write_global_brain_file) in a DIFFERENT view,
        // so a warm IndexedDB cache from an earlier visit is exactly the
        // stale-data case RepositoryTree's normal caching wasn't built for.
        // Live-confirmed: a file written via chat (vfs_write_global_brain_file)
        // was genuinely on vfs-service, but a Brain page whose tree had
        // already been cached before that write kept showing the old
        // snapshot indefinitely — see forceFreshTree's own comment.
        forceFreshTree
        autoOpenPath={requested.path}
        autoOpenLine={requested.line}
      />
    </div>
  </div>
  );
};

export default GlobalBrain;
